// 单标的 Agent 对话的纯文本格式化与解析。无 React、无副作用，可独立单测。
// 从 SingleAssetAgentDev.jsx 原样迁出，逻辑未改。
import { decimalMax4, money } from "../../utils/format";

export function nowLabel() {
  return new Intl.DateTimeFormat("zh-CN", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).format(new Date());
}

export function display(value) {
  return value === null || value === undefined || value === "" ? "—" : String(value);
}

export function affectedTradeCount(issues) {
  const affectedRows = new Set();
  issues.forEach((issue) => {
    if (issue.row_id) {
      affectedRows.add(`row:${issue.row_id}`);
    } else if (issue.row_number) {
      affectedRows.add(`number:${issue.row_number}`);
    }
  });
  return affectedRows.size;
}

export function isSystemDataIssue(issue) {
  return issue?.issue_category === "market_data" || issue?.user_correctable === false;
}

export function issueTitle(issues, isConfirmation) {
  const systemIssueCount = issues.filter(isSystemDataIssue).length;
  if (systemIssueCount > 0 && systemIssueCount === issues.length) {
    return "系统行情数据暂时不完整";
  }
  const affectedCount = affectedTradeCount(issues);
  if (!affectedCount) {
    return isConfirmation ? "存在成交价需要确认" : "交易草稿需要补充信息";
  }
  return isConfirmation
    ? `有 ${affectedCount} 笔交易的成交价需要确认`
    : `有 ${affectedCount} 笔交易无法可靠解析`;
}

export function assetTitle(rows) {
  const first = rows[0];
  if (!first) return "等待交易输入";
  const code = first.asset_code ? first.asset_code.split(".")[0] : "";
  return [code, first.asset_name || first.company_name].filter(Boolean).join(" ") || "标的待确认";
}

export function assetTypeLabel(row) {
  const type = String(row?.asset_type || "").toLowerCase();
  return type.includes("fund") || String(row?.asset_code || "").toUpperCase().endsWith(".OF")
    ? "基金"
    : "A股";
}

export function formattedTradeCell(header, value) {
  if (["数量/份额"].includes(header)) return decimalMax4(value);
  if (["成交价/净值"].includes(header)) return value === "—" ? value : `${decimalMax4(value)} 元`;
  if (["申购总额", "交易金额"].includes(header)) return value === "—" ? value : money(value);
  return value;
}

export function parseNaturalTradeLine(line) {
  const match = line.match(
    /^(\d+)\.\s+(\S+)\s+([^：]+)：([^，]+)，(?:成交价|净值)\s+([^，]+?)\s+元(?:，(?:申购金额|交易金额)\s+([^，]+?)\s+元)?，费用\s+(.+?)。?$/,
  );
  if (!match) return null;
  const quantityMatch = match[4].match(/^(.*?)\s*(股|份)$/);
  return [
    match[1],
    match[2],
    match[3],
    quantityMatch?.[1] || match[4],
    match[5],
    match[6] || "—",
    match[7],
    quantityMatch?.[2] || "",
  ];
}

// 判定和取值必须共用同一份同义词表。分成两套时，宽的那套（判定）先把表认成
// 交易表，窄的那套（取值）却取不到东西，于是整列渲染成 `--`——后端发的数据
// 完全正确（走查 2026-08-25：表头是 序号/日期/方向/数量/成交价，数字一个不缺，
// 前端全丢了）。见 AGENT-058。
const TRADE_COLUMN_ALIASES = {
  date: ["日期", "交易日期"],
  action: ["操作", "方向", "动作", "买卖方向", "买卖方向/股数"],
  quantity: ["数量", "数量/份额", "股数", "份额"],
  price: ["价格", "成交价", "成交价/净值", "净值", "单价"],
  unit: ["单位"],
};

function pickTradeCell(row, key) {
  for (const alias of TRADE_COLUMN_ALIASES[key]) {
    const value = row[alias];
    if (value !== undefined && value !== null && value !== "" && value !== "—") return value;
  }
  return undefined;
}

export function compactTradeTable(headers, rows) {
  if (headers.length === 3 && headers.includes("买卖方向/股数") && headers.includes("价格")) {
    return { headers, rows };
  }
  const has = (key) => headers.some((header) => TRADE_COLUMN_ALIASES[key].includes(header));
  // "交易" 是老格式那一列（`买入 100 股 @ 10 元`），单独认，它同时供方向/数量/价格。
  const hasLegacyTradeColumn = headers.includes("交易");
  const isTradeTable =
    has("date") && (hasLegacyTradeColumn || (has("action") && has("price")));
  if (!isTradeTable) return { headers, rows };
  return {
    headers: ["日期", "买卖方向/股数", "价格"],
    rows: rows.map((cells) => {
      const row = Object.fromEntries(headers.map((header, index) => [header, cells[index] || "—"]));
      const legacyTrade = String(row["交易"] || "").match(
        /^(.+?)\s+([\d,.]+)\s*(股|份)\s*@\s*(?:净值\s*)?([\d,.]+)\s*元$/,
      );
      const date = pickTradeCell(row, "date") ?? "—";
      const action = pickTradeCell(row, "action") ?? legacyTrade?.[1] ?? "—";
      const rawQuantity = pickTradeCell(row, "quantity") ?? legacyTrade?.[2];
      const rawPrice = pickTradeCell(row, "price") ?? legacyTrade?.[4];
      // 后端有时把单位写进数量格（"1,000 股"）。取值时剥掉，单位单独出。
      const quantityText = String(rawQuantity ?? "").replaceAll(",", "").trim();
      const inlineUnit = quantityText.match(/(股|份)\s*$/)?.[1];
      const quantity = decimalMax4(quantityText.replace(/(股|份)\s*$/, "").trim());
      const unit =
        pickTradeCell(row, "unit") ??
        inlineUnit ??
        legacyTrade?.[3] ??
        (/申购|赎回/.test(action) ? "份" : "股");
      const price = decimalMax4(String(rawPrice ?? "").replace(/\s*元\s*$/, "").trim());
      return [
        date,
        `${action} ${quantity} ${unit}`,
        `${price} 元`,
      ];
    }),
  };
}

export function formatTurnMetrics(timing, llmUsage, clientElapsedMs = null) {
  const durationMs = Number.isFinite(clientElapsedMs)
    ? clientElapsedMs
    : Number(timing?.api_total_ms ?? 0);
  const promptTokens = Number(llmUsage?.prompt_tokens ?? timing?.prompt_tokens ?? 0);
  const completionTokens = Number(llmUsage?.completion_tokens ?? timing?.completion_tokens ?? 0);
  return `耗时 ${(Math.max(0, durationMs) / 1000).toFixed(1)} 秒 · 上传 Token ${Math.max(0, promptTokens)} · 输出 Token ${Math.max(0, completionTokens)}`;
}

export function historyMessage(item) {
  const isAgent = item.role === "assistant";
  return {
    role: isAgent ? "agent" : "user",
    content: item.content,
    time: "",
    metrics: isAgent ? formatTurnMetrics(item.timing, item.llm_usage) : null,
  };
}

export function readableMessageBlocks(content) {
  const lines = String(content || "").split(/\r?\n/).filter(Boolean);
  const blocks = [];
  let naturalTradeRows = [];

  function flushNaturalTradeRows() {
    if (!naturalTradeRows.length) return;
    const compact = compactTradeTable(
      ["序号", "日期", "操作", "数量/份额", "成交价/净值", "交易金额", "费用", "单位"],
      naturalTradeRows,
    );
    blocks.push({ type: "table", ...compact });
    naturalTradeRows = [];
  }

  for (let index = 0; index < lines.length; index += 1) {
    const line = lines[index].trim();
    const nextLine = lines[index + 1]?.trim() || "";
    if (line.startsWith("|") && /^\|?\s*:?-{3,}/.test(nextLine)) {
      flushNaturalTradeRows();
      const headers = line.replace(/^\||\|$/g, "").split("|").map((cell) => cell.trim());
      const rows = [];
      index += 2;
      while (index < lines.length && lines[index].trim().startsWith("|")) {
        const cells = lines[index].trim().replace(/^\||\|$/g, "").split("|").map((cell) => cell.trim());
        rows.push(cells);
        index += 1;
      }
      blocks.push({ type: "table", ...compactTradeTable(headers, rows) });
      index -= 1;
      continue;
    }
    const naturalTrade = parseNaturalTradeLine(line);
    if (naturalTrade) {
      naturalTradeRows.push(naturalTrade);
      continue;
    }
    flushNaturalTradeRows();
    blocks.push({ type: "text", text: line });
  }
  flushNaturalTradeRows();
  return blocks;
}
